import { useEffect, useRef } from 'react';

/**
 * Custom hook for keyboard navigation in quick reply buttons
 * Supports Tab, Arrow keys, Enter, and Escape
 */
export function useKeyboardNavigation(
  isEnabled: boolean,
  onSelect?: (index: number) => void,
  itemCount: number = 0
) {
  const currentIndexRef = useRef<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isEnabled || itemCount === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const buttons = containerRef.current?.querySelectorAll<HTMLButtonElement>(
        'button:not([disabled])'
      );
      if (!buttons || buttons.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          e.preventDefault();
          currentIndexRef.current = (currentIndexRef.current + 1) % buttons.length;
          buttons[currentIndexRef.current]?.focus();
          break;

        case 'ArrowUp':
        case 'ArrowLeft':
          e.preventDefault();
          currentIndexRef.current =
            currentIndexRef.current <= 0
              ? buttons.length - 1
              : currentIndexRef.current - 1;
          buttons[currentIndexRef.current]?.focus();
          break;

        case 'Enter':
          if (document.activeElement && buttons.length > 0) {
            const activeButton = document.activeElement as HTMLButtonElement;
            if (Array.from(buttons).includes(activeButton)) {
              e.preventDefault();
              activeButton.click();
            }
          }
          break;

        case 'Escape':
          e.preventDefault();
          currentIndexRef.current = -1;
          (document.activeElement as HTMLElement)?.blur();
          break;

        case 'Home':
          e.preventDefault();
          currentIndexRef.current = 0;
          buttons[0]?.focus();
          break;

        case 'End':
          e.preventDefault();
          currentIndexRef.current = buttons.length - 1;
          buttons[buttons.length - 1]?.focus();
          break;
      }
    };

    const container = containerRef.current;
    container?.addEventListener('keydown', handleKeyDown);

    return () => {
      container?.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEnabled, itemCount, onSelect]);

  return containerRef;
}

/**
 * Custom hook for focus trap in modal/dialog
 * Keeps focus within the container when active
 */
export function useFocusTrap(isActive: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isActive) return;

    const container = containerRef.current;
    if (!container) return;

    const focusableElements = container.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    // Focus first element when trap activates
    firstElement?.focus();

    container.addEventListener('keydown', handleTabKey);

    return () => {
      container.removeEventListener('keydown', handleTabKey);
    };
  }, [isActive]);

  return containerRef;
}

/**
 * Custom hook for global keyboard shortcuts
 */
export function useKeyboardShortcut(
  key: string,
  callback: () => void,
  options: {
    ctrl?: boolean;
    shift?: boolean;
    alt?: boolean;
    meta?: boolean;
  } = {}
) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const { ctrl = false, shift = false, alt = false, meta = false } = options;

      const ctrlMatch = ctrl ? e.ctrlKey || e.metaKey : !e.ctrlKey && !e.metaKey;
      const shiftMatch = shift ? e.shiftKey : !e.shiftKey;
      const altMatch = alt ? e.altKey : !e.altKey;
      const metaMatch = meta ? e.metaKey : !e.metaKey;

      if (
        e.key.toLowerCase() === key.toLowerCase() &&
        ctrlMatch &&
        shiftMatch &&
        altMatch &&
        metaMatch
      ) {
        e.preventDefault();
        callback();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [key, callback, options]);
}
